import { ActionTitleActions } from "@/components/actionTitle/ActionTitleActions";
import { SectionItem } from "@/features/tasks/SectionItem";
import styled from "@emotion/styled";
import { type Opportunity } from "@repo/api";
import { Variables, chevronDownIcon, chevronUpIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import { FlexDiv } from "@repo/ui";
import {
  STORAGE_KEY,
  getStorageItem,
  setStorageItem,
} from "@repo/ui/src/storage/storage";
import { trackAnalytics } from "@repo/utils";
import { useState } from "react";

import { ProjectSummaryContent } from "./ProjectSummaryContent";
import { ProjectSummaryHeader } from "./ProjectSummaryHeader";
import { useAutoGenerateProjectSummary } from "./hooks";

interface ProjectSummarySectionProps {
  opportunity: Opportunity;
}

const CollapsibleSection = styled.div<{
  isOpen: boolean;
}>`
  display: grid;
  grid-template-rows: ${(props) => (props.isOpen ? "1fr" : "0fr")};
  overflow: hidden;
  transition: all ${Variables.durationStandardWeb} ${Variables.easeEasy};
`;

const InnerContent = styled(FlexDiv)`
  min-height: 0;
  gap: ${Variables.space100};
  flex-direction: column;
`;

const getCollapsibleStateFromStorage = () => {
  const res = getStorageItem(STORAGE_KEY.PROJECT_AI_SUMMARY);

  if (!res) {
    return [];
  }
  return JSON.parse(res) as number[];
};

export const ProjectSummarySection = ({
  opportunity,
}: ProjectSummarySectionProps) => {
  const [collapsibleOpen, setCollapsibleOpen] = useState<number[]>(
    getCollapsibleStateFromStorage
  );
  const { isGenerating, handleGenerate } =
    useAutoGenerateProjectSummary(opportunity);

  const isCollapsibleOpen = !collapsibleOpen.includes(opportunity.id);

  const handleCollapsibleClick = () => {
    const newStorageValue = collapsibleOpen.includes(opportunity.id)
      ? collapsibleOpen.filter((el) => el !== opportunity.id)
      : [...collapsibleOpen, opportunity.id];

    setCollapsibleOpen(newStorageValue);
    setStorageItem(
      STORAGE_KEY.PROJECT_AI_SUMMARY,
      JSON.stringify(newStorageValue)
    );
    trackAnalytics("google_ai.expand_collapse_summary");
  };

  return (
    <>
      <SectionItem
        title={t("label.ai_project_summary")}
        action={
          <ActionTitleActions
            actions={[
              {
                id: "collapse-expand-action",
                icon: isCollapsibleOpen ? chevronUpIcon : chevronDownIcon,
                onClick: handleCollapsibleClick,
                text: isCollapsibleOpen
                  ? t("action.collapse_section")
                  : t("action.expand_section"),
                iconOnly: true,
              },
            ]}
          />
        }
      >
        <CollapsibleSection isOpen={isCollapsibleOpen}>
          <InnerContent>
            <ProjectSummaryHeader
              opportunity={opportunity}
              isGenerating={isGenerating}
              onGenerate={handleGenerate}
            />

            <ProjectSummaryContent opportunity={opportunity} />
          </InnerContent>
        </CollapsibleSection>
      </SectionItem>
    </>
  );
};
